// Plain-language helpers shared by the Skills screens: skill names as
// words, who a skill applies to, and API errors as one readable line.

import type { LearnedSkill } from "@/api/skills";
import { ApiError } from "@/lib/api";
import { humanizeSlug } from "@/lib/format";

/**
 * A skill's name as words. Agents name skills with slugs
 * ("browser-cdp-fallback"); those read as "Browser CDP fallback", with
 * spellings taken from the description. A name a person typed (it has
 * spaces) is kept as it is.
 */
export function skillName(name: string | null | undefined, context?: string | null): string {
  const raw = (name ?? "").trim();
  if (!raw || /\s/.test(raw)) return raw;
  const words = humanizeSlug(raw.toLowerCase(), context, { acronyms: true }) || raw;
  return restoreProperNouns(words, context);
}

/**
 * Words the description capitalises mid-sentence are proper nouns
 * ("Google Workspace", "Companies House"): give the name the same
 * spelling. humanizeSlug leaves words inside such a run lower-case.
 */
function restoreProperNouns(name: string, context?: string | null): string {
  if (!context) return name;
  const proper = new Map<string, string>();
  const tokens = context.split(/\s+/);
  for (let i = 1; i < tokens.length; i++) {
    // A capital after a sentence end is just the start of a sentence.
    if (/[.!?:]["”')]?$/.test(tokens[i - 1]!)) continue;
    const word = /^["“'(]?([A-Z][a-z]+)\b/.exec(tokens[i]!)?.[1];
    if (word) proper.set(word.toLowerCase(), word);
  }
  return name
    .split(" ")
    .map((w, i) => (i > 0 && proper.get(w) ? proper.get(w)! : w))
    .join(" ");
}

export function isDefaultSkill(skill: Pick<LearnedSkill, "source">): boolean {
  return skill.source?.startsWith("default:") ?? false;
}

export function isLibrarySkill(skill: Pick<LearnedSkill, "source">): boolean {
  return skill.source?.startsWith("clawhub:") ?? false;
}

/**
 * The scope chip on an installed skill: nothing for the normal case (every
 * agent), and only the exception: "Only Marcus", "Julia, Priya",
 * "Julia, Priya and 2 more".
 */
export function skillLimit(
  skill: Pick<LearnedSkill, "scope" | "assigned_agents" | "author_template">,
  nameOf: (template: string) => string,
): string | null {
  if (skill.scope === "private") return `Only ${nameOf(skill.author_template)}`;
  if (skill.scope !== "customer") return humanizeSlug(skill.scope);
  const names = (skill.assigned_agents ?? []).filter(Boolean).map(nameOf);
  if (names.length === 0) return null;
  if (names.length === 1) return `Only ${names[0]}`;
  if (names.length <= 3) return names.join(", ");
  return `${names.slice(0, 2).join(", ")} and ${names.length - 2} more`;
}

/**
 * Who a skill applies to, where a column must always say something (the
 * review table): skillLimit's words, or "All agents" for the normal case.
 */
export function skillAudience(
  skill: Pick<LearnedSkill, "scope" | "assigned_agents" | "author_template">,
  nameOf: (template: string) => string,
): string {
  return skillLimit(skill, nameOf) ?? "All agents";
}

/** The machine code of a failed request ("empty_body", "already_promoted"), if it has one. */
export function errorCode(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body as { detail?: unknown } | null;
  const detail = body && typeof body === "object" && "detail" in body ? body.detail : null;
  if (detail && typeof detail === "object") return (detail as { error?: string }).error ?? null;
  return typeof detail === "string" ? detail : null;
}

// Codes whose server message is written for developers.
const PLAIN: Record<string, string> = {
  empty_body: "The proposal arrived without its procedure, so there is nothing to install.",
  changed_meanwhile: "It changed meanwhile. Try again.",
};

/** One readable line for a failed request ("already_promoted — proposal … is already #12" → the message). */
export function describeError(err: unknown): string {
  const code = errorCode(err);
  if (code && PLAIN[code]) return PLAIN[code]!;
  if (err instanceof ApiError) {
    const body = err.body as { detail?: unknown } | null;
    const detail = body && typeof body === "object" && "detail" in body ? body.detail : null;
    if (detail && typeof detail === "object") {
      const d = detail as { error?: string; message?: string };
      return d.message || d.error || err.message;
    }
    if (typeof detail === "string") return humanizeSlug(detail);
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

/** The Tier 2 id a workshop proposal already went to, from a 409 "already_promoted". */
export function alreadyPromotedId(err: unknown): number | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as { detail?: { error?: string; message?: string } } | null;
  const detail = body?.detail;
  if (!detail || detail.error !== "already_promoted") return null;
  const m = /#(\d+)/.exec(detail.message ?? "");
  return m ? Number(m[1]) : null;
}
